// =============================================================================
// domain/excursionEdge.ts — the ENTRY EDGE measures.
// -----------------------------------------------------------------------------
// Three of the old client's eleven excursion cards, and the three that answer one
// question: **is the setup even worth trading?** They read what the market
// OFFERED — room, heat and tempo — independent of how the trade was exited. If
// the entry has no edge here, nothing downstream can save it.
//
//   E-Ratio           avg MFE ÷ avg MAE. Curtis Faith's, simplified.
//   MAE / Stop Ratio  where in the planned stop range winners actually hurt.
//   Temporal Profile  how fast it rips, how fast it fails.
//
// ── 🚨 EVERY ONE IS SCOPED TO ONE INSTRUMENT BY THE CALLER ──
//
// MAE and MFE are stored in POINTS, and `excursionStats.ts` states the rule: 1.5
// points on CL is not 1.5 points on ES, so aggregating across instruments
// produces confident, meaningless numbers. Nothing here mixes them for the
// caller — the scoping is the caller's obligation, exactly as it is next door.
//
// ── ⚠ EACH GROUPS BY PATTERN **OR** BY EXIT MODEL, AND THE MATHS IS IDENTICAL ──
//
// The old client duplicates each function's body per grouping. Here the group key
// is a parameter, because the two views differing by accident is a whole class of
// bug — and the old client has one: three of its cards carry a near-identical
// `median` helper defined three times.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// Everything is a function of the trades handed in.
// =============================================================================

import { median } from './excursionStats';
import { tradeValue, type ContractSpec } from './trade';
import type { PnlUnit } from './types';

/** What these three measures need from a trade. */
export interface EdgeTrade {
  patternId: string | null;
  exitModelId: string | null;
  /**
   * ⚠ CONTRACT AND POINTS ARE HERE FOR THE P&L COLUMN, nothing else (#67).
   *
   * The table's P&L follows the display-unit switch, and every unit but dollars
   * needs one of these: points reads `points`, ticks needs the contract's tick
   * size, R reads `rrRealized`. Without them the column summed raw dollars and
   * the header still said "pts".
   */
  contract: string | null;
  points: number | null;
  maePoints: number | null;
  mfePoints: number | null;
  /** Seconds from entry to the worst adverse print. */
  maeSecs: number | null;
  /** Seconds from entry to the best favourable print. */
  mfeSecs: number | null;
  riskPoints: number | null;
  netPnl: number | null;
  /**
   * Realised R as the trade recorded it.
   *
   * ⚠ READ, NOT RE-DERIVED from points ÷ risk. The stored figure is what every
   * other surface prints, and a second derivation here would disagree on any
   * trade whose risk was edited after the fact.
   *
   * ⚠ It moved UP here from `DiagnoseTrade` when the P&L column started
   * following the display unit: in R that column has no other source.
   */
  rrRealized: number | null;
}

export type GroupBy = 'pattern' | 'exit-model';

/** Resolves a group id to the name shown. Returning null DROPS the row — a
 *  pattern that no longer exists must not appear as a raw id. */
export type NameLookup = (id: string) => string | null;

export function groupKeyOf(t: EdgeTrade, by: GroupBy): string | null {
  return by === 'exit-model' ? t.exitModelId : t.patternId;
}

/**
 * Bucket trades by pattern or exit model, dropping anything unresolvable.
 *
 * ⚠ A trade with no group is DROPPED, not collected into an "(untagged)" row.
 * These cards are about a setup's edge; an untagged bucket is a mixture of every
 * setup and its E-Ratio means nothing.
 */
function groupTrades(
  trades: readonly EdgeTrade[], by: GroupBy, nameOf: NameLookup,
): Map<string, { name: string; trades: EdgeTrade[] }> {
  const out = new Map<string, { name: string; trades: EdgeTrade[] }>();
  for (const t of trades) {
    const id = groupKeyOf(t, by);
    if (!id) continue;
    const name = nameOf(id);
    if (name == null) continue;
    const slot = out.get(id) ?? { name: name || '(Untitled)', trades: [] };
    slot.trades.push(t);
    out.set(id, slot);
  }
  return out;
}

// ---------------------------------------------------------------------------
// 1. E-Ratio
// ---------------------------------------------------------------------------

/** Below this a group is not shown at all — both averages swing wildly. */
export const ERATIO_MIN_SAMPLE = 5;
/** A headline "best / worst" claim wants more than the display floor. */
export const ERATIO_HEADLINE_SAMPLE = 10;
/**
 * ⚠ POINTS, and it is a divide-by-near-zero guard rather than a rounding one.
 * A group whose average adverse excursion is under a tenth of a point has
 * effectively never gone against you in the sample; dividing by it produces a
 * five-figure ratio that is arithmetically true and analytically noise.
 */
export const ERATIO_MAE_FLOOR = 0.1;

export type EdgeVerdict = 'strong' | 'marginal' | 'negative';

export interface EratioRow {
  id: string;
  name: string;
  /** Trades carrying BOTH excursions. Not the group's trade count. */
  sample: number;
  wins: number;
  losses: number;
  winRatePct: number;
  avgMae: number;
  avgMfe: number;
  /** `Infinity` when the group's average heat is under the floor. */
  eratio: number;
  /**
   * The group's P&L IN THE DISPLAY UNIT — named for it, because it was wrong
   * for a month while it was called `netPnl` (#67).
   *
   * ⚠ Trades with no value in the unit are SKIPPED, not zeroed — a stop-less
   * trade has no R, and folding a zero in would pull the total toward nothing.
   * That is what beta does, and what `computeLeaderboardStats` does.
   */
  unitPnl: number;
  verdict: EdgeVerdict;
}

export interface EratioResult {
  rows: EratioRow[];
  /** Best and worst by E-Ratio, preferring groups with a real sample. */
  best: EratioRow | null;
  worst: EratioRow | null;
  /** Mean E-Ratio across groups with a FINITE one. Null when none has. */
  averageEratio: number | null;
}

export function edgeVerdict(e: number): EdgeVerdict {
  if (!Number.isFinite(e) || e >= 1.5) return 'strong';
  if (e >= 1.0) return 'marginal';
  return 'negative';
}

/**
 * Average favourable excursion over average adverse excursion, per group.
 *
 * ⚠ AVERAGES, NOT MEDIANS, and that is the definition rather than an oversight.
 * The E-Ratio is a ratio of expected magnitudes: a setup that occasionally rips
 * five times its usual distance genuinely has more room to run, and a median
 * would discard exactly that. The sample floor is what keeps it honest instead.
 *
 * ⚠ BOTH EXCURSIONS ARE TAKEN AS MAGNITUDES. They are stored unsigned in this
 * product, but `Math.abs` is applied rather than assumed — a single negative MAE
 * from an older import would otherwise cancel against the rest of the sum and
 * inflate the ratio, which is the direction that says "press this setup".
 */
export function eratio(
  trades: readonly EdgeTrade[], by: GroupBy, nameOf: NameLookup,
  catalogue: readonly ContractSpec[], unit: PnlUnit,
): EratioResult {
  const rows: EratioRow[] = [];

  for (const [id, g] of groupTrades(trades, by, nameOf)) {
    const mae: number[] = [];
    const mfe: number[] = [];
    let wins = 0; let losses = 0; let unitPnl = 0;

    for (const t of g.trades) {
      const a = t.maePoints; const f = t.mfePoints;
      if (a == null || f == null || !Number.isFinite(a) || !Number.isFinite(f)) continue;
      mae.push(Math.abs(a));
      mfe.push(Math.abs(f));
      // ⚠ THE SIGN COMES FROM DOLLARS, THE MAGNITUDE FROM THE UNIT. A winner is
      // a winner in every unit, and reading the sign off an R that a stop-less
      // trade does not have would silently drop it from the win rate.
      const p = Number(t.netPnl) || 0;
      if (p > 0) wins++; else if (p < 0) losses++;
      const uv = tradeValue(t, unit, catalogue);
      if (uv != null) unitPnl += uv;
    }

    if (mae.length < ERATIO_MIN_SAMPLE) continue;

    const avgMae = mae.reduce((s, x) => s + x, 0) / mae.length;
    const avgMfe = mfe.reduce((s, x) => s + x, 0) / mfe.length;
    const e = avgMae < ERATIO_MAE_FLOOR ? Infinity : avgMfe / avgMae;
    const decided = wins + losses;

    rows.push({
      id, name: g.name, sample: mae.length, wins, losses,
      winRatePct: decided ? (wins / decided) * 100 : 0,
      avgMae, avgMfe, eratio: e, unitPnl, verdict: edgeVerdict(e),
    });
  }

  // ⚠ THE HEADLINE PREFERS A REAL SAMPLE. Naming a five-trade group as the best
  // setup in the book is how a coaching surface sends someone to press an edge
  // that does not exist. Falls back to the whole set only when nothing clears
  // the higher bar, so the tiles never go blank while rows are on screen.
  const sized = rows.filter((r) => r.sample >= ERATIO_HEADLINE_SAMPLE);
  const ranked = sized.length ? sized : rows;
  const byEdge = [...ranked].sort((a, b) => rank(b.eratio) - rank(a.eratio));

  const finite = rows.filter((r) => Number.isFinite(r.eratio));
  return {
    rows,
    best: byEdge[0] ?? null,
    worst: byEdge.length ? byEdge[byEdge.length - 1]! : null,
    averageEratio: finite.length
      ? finite.reduce((s, r) => s + r.eratio, 0) / finite.length : null,
  };
}

/** Infinity sorts above every real ratio without poisoning the comparison. */
const rank = (e: number): number => (Number.isFinite(e) ? e : 99);

// ---------------------------------------------------------------------------
// 2. MAE / Stop Ratio distribution
// ---------------------------------------------------------------------------

/** Fewer winners than this and the shape is not a shape. */
export const MAE_STOP_MIN_WINNERS = 5;

export interface MaeStopBand {
  label: string;
  /** Lower bound as a share of the planned stop, inclusive. */
  lo: number;
  /** Upper bound, exclusive. The last band's is over 1 so 100% lands in it. */
  hi: number;
}

export const MAE_STOP_BANDS: readonly MaeStopBand[] = [
  { label: '0–20%', lo: 0, hi: 0.2 },
  { label: '20–40%', lo: 0.2, hi: 0.4 },
  { label: '40–60%', lo: 0.4, hi: 0.6 },
  { label: '60–80%', lo: 0.6, hi: 0.8 },
  { label: '80–100%', lo: 0.8, hi: 1.01 },
];

/**
 * A ratio above 100% lands in the TOP band.
 *
 * That happens when a trade went past its own stop and still closed green —
 * slippage through the stop, or a stop that was moved. Rare, real, and it belongs
 * at the top rather than in a sixth band or dropped: the card asks how close to
 * the stop winners came, and for those the answer is "all the way".
 *
 * ⚠ THE FINAL `return` IS WHAT DOES THAT, not a cap. The old client opens this
 * with `const capped = Math.min(r, 1.0)` and it is DEAD CODE in both clients —
 * anything over the last band's bound already falls out of the loop into the same
 * answer. It was relocated here verbatim and a deliberate sabotage removing it
 * changed nothing, which is how it was found. Deleted rather than kept, for the
 * same reason as the redundant spread in `saveAccount`: a line that cannot change
 * the outcome reads as the thing producing it, and the next person to add a sixth
 * band will trust the wrong mechanism.
 */
export function maeStopBandOf(ratio: number): number {
  for (let i = 0; i < MAE_STOP_BANDS.length; i += 1) {
    if (ratio < (MAE_STOP_BANDS[i] as MaeStopBand).hi) return i;
  }
  return MAE_STOP_BANDS.length - 1;
}

export type StopVerdict = 'slack' | 'balanced' | 'tight';

export interface MaeStopRow {
  id: string;
  name: string;
  /** Winners with both an MAE and a planned stop. */
  sample: number;
  /** One count per band, in `MAE_STOP_BANDS` order. */
  counts: number[];
  /** The same, as shares of the sample — what a stacked bar draws. */
  shares: number[];
  medianRatio: number | null;
  /** Share sitting in the lowest band: winners that barely tagged the stop. */
  slackShare: number;
  /** Share in the top two bands: winners that came close to being stopped. */
  tightShare: number;
  verdict: StopVerdict;
}

/**
 * Where in the planned stop range the WINNERS actually hurt.
 *
 * ⚠ WINNERS ONLY, and the old client's reason is exactly right: a loser either
 * hit the stop or was cut before it, so its adverse excursion says nothing about
 * whether the stop was well placed. Including them would drag every distribution
 * toward the top band and recommend widening a stop that is already too wide.
 */
export function maeStopRatio(
  trades: readonly EdgeTrade[], by: GroupBy, nameOf: NameLookup,
): MaeStopRow[] {
  const rows: MaeStopRow[] = [];

  for (const [id, g] of groupTrades(trades, by, nameOf)) {
    const ratios: number[] = [];
    for (const t of g.trades) {
      if ((Number(t.netPnl) || 0) <= 0) continue;
      const mae = t.maePoints;
      const stop = t.riskPoints;
      if (mae == null || !Number.isFinite(mae)) continue;
      if (!stop || stop <= 0) continue;
      ratios.push(Math.abs(mae) / stop);
    }
    if (ratios.length < MAE_STOP_MIN_WINNERS) continue;

    const counts = MAE_STOP_BANDS.map(() => 0);
    for (const r of ratios) counts[maeStopBandOf(r)] = (counts[maeStopBandOf(r)] ?? 0) + 1;
    const n = ratios.length;
    const shares = counts.map((c) => c / n);
    const slackShare = (shares[0] ?? 0);
    const tightShare = (shares[3] ?? 0) + (shares[4] ?? 0);

    rows.push({
      id, name: g.name, sample: n, counts, shares,
      medianRatio: median(ratios),
      slackShare, tightShare,
      verdict: slackShare >= 0.5 ? 'slack' : tightShare >= 0.5 ? 'tight' : 'balanced',
    });
  }
  return rows;
}

// ---------------------------------------------------------------------------
// 3. Temporal profile
// ---------------------------------------------------------------------------

export const TEMPO_MIN_SAMPLE = 5;
/** Under two minutes to the extreme: scalp tempo. */
export const TEMPO_FAST_SECS = 120;
/**
 * Over eight minutes: grinder tempo.
 *
 * ⚠ The old client's comment records that this was RECALIBRATED from fifteen
 * minutes because ">15min almost never fired" on intraday futures. Ported with
 * the number it settled on, not the one it started with.
 */
export const TEMPO_SLOW_SECS = 480;

export type Tempo = 'fast' | 'mid' | 'slow';

export interface TemporalRow {
  id: string;
  name: string;
  /** Trades carrying BOTH timings — the row's population. */
  sample: number;
  /** Winners' median seconds to the favourable peak. */
  medMfeSecs: number | null;
  /** Losers' median seconds to the adverse extreme. */
  medMaeSecs: number | null;
  /** Winners' median favourable reach, points. */
  medMfePoints: number | null;
  /** Losers' median adverse reach, points. */
  medMaePoints: number | null;
  mfeTempo: Tempo;
  maeTempo: Tempo;
  /** The verdict in words — what the trader acts on. */
  label: string;
}

export function tempoOf(secs: number | null): Tempo {
  if (secs == null) return 'mid';
  if (secs < TEMPO_FAST_SECS) return 'fast';
  if (secs > TEMPO_SLOW_SECS) return 'slow';
  return 'mid';
}

/**
 * The tempo verdict, in the order the old client ranks them: most
 * coaching-actionable first.
 *
 * ⚠ "Quick rip · slow bleed" is the worst combination in the product and is
 * checked first — the favourable move is gone before you can act on it and the
 * adverse one takes long enough to talk yourself into holding.
 */
export function tempoLabel(mfe: Tempo, mae: Tempo): string {
  if (mfe === 'fast' && mae === 'slow') return 'Quick rip · slow bleed';
  if (mfe === 'slow' && mae === 'fast') return 'Patient build · clean fail';
  if (mfe === 'fast' && mae === 'fast') return 'Sharp move · fast both ways';
  if (mfe === 'slow' && mae === 'slow') return 'Slow grinder · develops over time';
  if (mfe === 'fast') return 'Fast mover · quick partials fit';
  if (mfe === 'slow') return 'Slow grind · runner-friendly';
  if (mae === 'fast') return 'Clean fail · trust the stop';
  if (mae === 'slow') return 'Slow bleed · tighten or bail';
  return 'Mid-tempo';
}

/**
 * How fast each group rips, and how fast it fails.
 *
 * ── 🚨 THE TWO SIDES ARE MEASURED ON DIFFERENT TRADES, DELIBERATELY ──
 *
 * Time-to-MFE and median favourable reach come from the **winners**: that is how
 * the move develops when the setup works. Time-to-MAE and adverse reach come from
 * the **losers**: that is how it fails when it does not. Mixing them in — a
 * loser's brief favourable tick, a winner's early heat — measures the wiggle
 * rather than the move, and both medians drift toward the middle where every
 * group reads "mid-tempo" and the card says nothing.
 *
 * ⚠ The SAMPLE is the group's trades carrying both timings, so a row can report
 * a sample of 12 while its MFE side rests on 4 winners. That is the old client's
 * shape and the honest one — the count describes the row, and each side's own
 * median is null when its outcome never occurred.
 */
export function temporalProfile(
  trades: readonly EdgeTrade[], by: GroupBy, nameOf: NameLookup,
): TemporalRow[] {
  const rows: TemporalRow[] = [];

  for (const [id, g] of groupTrades(trades, by, nameOf)) {
    const winMfeSecs: number[] = [];
    const loseMaeSecs: number[] = [];
    const winMfePoints: number[] = [];
    const loseMaePoints: number[] = [];
    let sample = 0;

    for (const t of g.trades) {
      const a = t.maeSecs; const f = t.mfeSecs;
      if (a == null || f == null || !Number.isFinite(a) || !Number.isFinite(f)) continue;
      sample += 1;
      if ((Number(t.netPnl) || 0) > 0) {
        winMfeSecs.push(f);
        winMfePoints.push(Math.abs(Number(t.mfePoints) || 0));
      } else {
        loseMaeSecs.push(a);
        loseMaePoints.push(Math.abs(Number(t.maePoints) || 0));
      }
    }

    if (sample < TEMPO_MIN_SAMPLE) continue;

    const medMfeSecs = median(winMfeSecs);
    const medMaeSecs = median(loseMaeSecs);
    const mfeTempo = tempoOf(medMfeSecs);
    const maeTempo = tempoOf(medMaeSecs);

    rows.push({
      id, name: g.name, sample,
      medMfeSecs, medMaeSecs,
      medMfePoints: median(winMfePoints),
      medMaePoints: median(loseMaePoints),
      mfeTempo, maeTempo,
      label: tempoLabel(mfeTempo, maeTempo),
    });
  }
  return rows;
}

/** `7:05`, `0:42`, `—`. Minutes and seconds, as the old client prints them. */
export function durationLabel(secs: number | null): string {
  if (secs == null || !Number.isFinite(secs)) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
